import {
  DataTable,
  Dialog,
  FormInput,
  FormLabel,
  FormSelect,
  Heading,
  Loader,
} from "@/components";
import usePaymentListing from "./useListing";
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui";
import { FormProvider } from "react-hook-form";
import { monthOptions } from "@/lib/utils";
import {
  frequencyOptions,
  parkDocumentSearch,
  transactionTypeOptions,
  SEARCH_CRITERIA,
  SEARCH_TYPE,
  searchType,
} from "../../consts";

const Listing = () => {
  const {
    breadcrumbData,
    columns,
    globalFilter,
    setGlobalFilter,
    onRowClick,
    isDocLoading,
    documentList,
    setPageNumber,
    setPageSize,
    pageNumber,
    pageSize,
    isDocRefetching,
    form,
    onSubmit,
    requiredFields,
    onClear,
    communityDetail,
    onMonthChange,
    month,
    monthKey,
    isLoadingBillingCategory,
    billingCategory,
    currentFinanceYear,
    openDeleteDialog,
    setOpenDeleteDialog,
    selectedRow,
    isDeleteDocumentPending,
    onDeleteConfirm,
    sorting,
    setSorting,
  } = usePaymentListing();
  const { errors } = form.formState;

  return (
    <div className="p-4 md:p-6">
      {/* Header & Search */}
      <div className="flex flex-col xl:flex-row gap-2 xl:items-center xl:justify-between mb-6">
        <div className="flex flex-row items-center justify-between">
          <Heading
            title={<span>Pending for Posting - {communityDetail?.name} <span className="text-[12px]">(FY-{currentFinanceYear})</span></span>}
            breadcrumbData={breadcrumbData}
          />
          {/* {isMobile && <Button className="px-2">+ Add Budget</Button>} */}
        </div>
        <div className="relative mb-4 md:w-1/5 lg:w-1/10 mt-2">
          <FormLabel>Month</FormLabel>
          <Select onValueChange={onMonthChange} key={monthKey}>
            <SelectTrigger className="w-full bg-white">
              <SelectValue
                placeholder={
                  monthOptions?.find((data: any) => data.value == month)?.label
                }
              />
            </SelectTrigger>
            <SelectContent>
              {monthOptions?.map((data: any) => (
                <SelectItem key={data?.value} value={data?.value}>
                  {data?.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {isLoadingBillingCategory ? (
        <></>
      ) : (
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="bg-white mt-[20px] rounded-lg px-[30px] py-[25px] mb-[30px]">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[30px]">
                <FormSelect
                  name={SEARCH_TYPE}
                  control={form.control}
                  label="Search Type"
                  placeholder="Please select"
                  error={errors[SEARCH_TYPE]?.message}
                  isRequired={requiredFields.includes(SEARCH_TYPE)}
                  options={[...parkDocumentSearch, ...searchType]}
                />
                {form.watch(SEARCH_TYPE) == "billingCategoryIds" ? (
                  <FormSelect
                    name={SEARCH_CRITERIA}
                    control={form.control}
                    label="Billing Category"
                    placeholder="Please select"
                    error={errors[SEARCH_CRITERIA]?.message}
                    isRequired={requiredFields.includes(SEARCH_CRITERIA)}
                    options={(billingCategory?.data || [])?.map(
                      (data: any) => ({
                        value: data?.billingCategoryId?.toString(),
                        label: data?.categoryName,
                      })
                    )}
                  />
                ) : form.watch(SEARCH_TYPE) == "transactionType" ? (
                  <FormSelect
                    name={SEARCH_CRITERIA}
                    control={form.control}
                    label="Transaction Type"
                    placeholder="Please select"
                    error={errors[SEARCH_CRITERIA]?.message}
                    isRequired={requiredFields.includes(SEARCH_CRITERIA)}
                    options={transactionTypeOptions}
                  />
                ) : form.watch(SEARCH_TYPE) == "frequency" ? (
                  <FormSelect
                    name={SEARCH_CRITERIA}
                    control={form.control}
                    label="Frequency"
                    placeholder="Please select"
                    error={errors[SEARCH_CRITERIA]?.message}
                    isRequired={requiredFields.includes(SEARCH_CRITERIA)}
                    options={frequencyOptions}
                  />
                ) : (
                  <FormInput
                    name={SEARCH_CRITERIA}
                    control={form.control}
                    label="Search Criteria"
                    placeholder="Please enter"
                    error={errors[SEARCH_CRITERIA]?.message}
                    isRequired={requiredFields.includes(SEARCH_CRITERIA)}
                  />
                )}

                <div className="flex flex-row justify-end gap-3 pt-3">
                  <Button type="submit">Search</Button>
                  <Button variant="outline" type="button" onClick={onClear}>
                    Clear
                  </Button>
                </div>
              </div>
            </div>
          </form>
        </FormProvider>
      )}

      {/* Responsive Table */}
      {isDocLoading || isDocRefetching || isLoadingBillingCategory ? (
        <Loader />
      ) : (
        <>
          <DataTable
            columns={columns}
            data={documentList?.data || []}
            setGlobalFilter={setGlobalFilter}
            globalFilter={globalFilter}
            onRowClick={onRowClick}
            setPageNumber={setPageNumber}
            setPageSize={setPageSize}
            pageNumber={pageNumber}
            pageSize={pageSize}
            totalPages={Math.ceil(documentList?.total / pageSize)}
            totalRecords={documentList?.total}
            serverSidePagination={true}
            sorting={sorting}
            onSortingChange={setSorting}
          />
        </>
      )}
      {openDeleteDialog && (
        <Dialog
          open={openDeleteDialog}
          setOpen={setOpenDeleteDialog}
          title={
            <div className="text-2xl font-bold text-primary">
              {"Confirmation"}
              <div className="h-1 w-[30%] bg-primary rounded-full" />
            </div>
          }
          saveText="Delete"
          saveBtnClassName="bg-red-500 text-white hover:bg-red-500 hover:text-white"
          isLoading={isDeleteDocumentPending}
          onSave={onDeleteConfirm}
        >
          <p className="text-sm">
            Are you sure you want to delete the Pending Posting -{" "}
            <span className="font-bold text-primary">
              {(selectedRow as any)?.parkedDocumentHeaderId}
            </span>
            ?
          </p>
        </Dialog>
      )}
    </div>
  );
};

export default Listing;
